import { textColorFields } from '../_shared/textColor.js';
import { universalFields, mediaRadiusField } from '../_shared/universal.js';
/**
 * spec-comparison — a multi-PRODUCT specification comparison table (the "which product is right for
 * you?" PDP anatomy): a left label column + up to 4 product columns. Each `column` block is one
 * product (render image → boxed name); each `spec-row` block is one spec line (label + an icon and a
 * value per column).
 *
 * There is deliberately NO variant/price cell: colours, sizes and prices belong to the product page,
 * not to a comparison table, and a hand-typed copy of them here only drifts from the catalog (founder
 * call 2026-08-01). Stored documents may still carry the removed keys — they are simply ignored.
 *
 * Distinct from `comparison-table` on purpose: that section is the Shrine benefit checklist ("us vs a
 * generic brand", ✓/✗ per row) and stays a verbatim port; THIS one compares real sibling products on
 * their specs. Purely presentational — values are seller-typed text, so it works for any catalog
 * (headphones, cosmetics, bikes) without a data dependency.
 */
export const specComparisonSchema = {
    type: 'spec-comparison',
    name: 'Product spec comparison',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            group: 'General',
            info: 'Adds an anchor id to this section so a link can scroll to it.',
            default: false,
        },
        {
            id: 'title',
            type: 'richtext',
            label: 'Heading',
            group: 'General',
            default: { format: 'html', html: 'Which product is right for you?' },
        },
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'General',
            default: 'h2',
            options: [
                { value: 'h1', label: 'Large' },
                { value: 'h2', label: 'Medium' },
                { value: 'h3', label: 'Small' },
            ],
        },
        {
            id: 'productRowLabel',
            type: 'text',
            label: 'Product row label',
            group: 'General',
            default: 'Product',
            info: 'Label of the product-name row. Nothing in this table is painted from a hard-coded string — leave it empty to hide the label.',
            maxLength: 60,
        },
        {
            id: 'labelColumnWidth',
            type: 'range',
            label: 'Label column width',
            group: 'Layout',
            min: 120,
            max: 320,
            step: 10,
            default: 220,
            unit: 'px',
        },
        {
            id: 'productImageHeight',
            type: 'range',
            label: 'Product image height',
            group: 'Layout',
            min: 80,
            max: 280,
            step: 10,
            default: 160,
            unit: 'px',
        },
        {
            id: 'cellIconSize',
            type: 'range',
            label: 'Spec icon size',
            group: 'Layout',
            min: 20,
            max: 64,
            step: 2,
            default: 36,
            unit: 'px',
        },
        {
            id: 'buttonLabel',
            type: 'text',
            label: 'Button label',
            group: 'Button',
            default: 'Compare more products',
            defaultKey: 'section.compare_more',
            info: 'Small outline button under the table. Leave empty to hide it.',
            maxLength: 60,
        },
        {
            id: 'buttonLink',
            type: 'link',
            label: 'Button link',
            group: 'Button',
        },
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
    accepts: ['column', 'spec-row'],
    blocks: [
        {
            type: 'column',
            name: 'Product column',
            max: 4,
            fields: [
                { id: 'image', type: 'image', label: 'Product image', group: 'Product' },
                {
                    id: 'name',
                    type: 'text',
                    label: 'Product name',
                    group: 'Product',
                    default: 'Product name',
                    defaultKey: 'section.product_name',
                    maxLength: 80,
                },
                { id: 'link', type: 'link', label: 'Product link', group: 'Product' },
            ],
        },
        {
            type: 'spec-row',
            name: 'Spec row',
            max: 12,
            fields: [
                {
                    id: 'label',
                    type: 'text',
                    label: 'Row label',
                    group: 'Content',
                    default: 'Specification',
                    maxLength: 80,
                },
                {
                    // MUST be `icon-name` (not `text`): collectIconNames only reads icon-name fields, so a text
                    // field's glyphs would be missing from the storefront's `icon_names=` font SUBSET and render
                    // as raw ligature text ("BATTERY_FULL") on any page that ships the subset.
                    id: 'icon',
                    type: 'icon-name',
                    label: 'Icon (Material Symbol name)',
                    group: 'Content',
                    default: '',
                    info: 'Glyph name (e.g. battery_full, water_drop). Shown above every value of the row; per-cell images below override it.',
                },
                {
                    id: 'value1',
                    type: 'richtext',
                    label: 'Value — product 1',
                    group: 'Values',
                    default: { format: 'html', html: '' },
                },
                {
                    id: 'value2',
                    type: 'richtext',
                    label: 'Value — product 2',
                    group: 'Values',
                    default: { format: 'html', html: '' },
                },
                {
                    id: 'value3',
                    type: 'richtext',
                    label: 'Value — product 3',
                    group: 'Values',
                    default: { format: 'html', html: '' },
                },
                {
                    id: 'value4',
                    type: 'richtext',
                    label: 'Value — product 4',
                    group: 'Values',
                    default: { format: 'html', html: '' },
                },
                { id: 'image1', type: 'image', label: 'Icon image — product 1', group: 'Icon images' },
                { id: 'image2', type: 'image', label: 'Icon image — product 2', group: 'Icon images' },
                { id: 'image3', type: 'image', label: 'Icon image — product 3', group: 'Icon images' },
                { id: 'image4', type: 'image', label: 'Icon image — product 4', group: 'Icon images' },
            ],
        },
    ],
    maxBlocks: 16,
};
export const specComparisonPresets = [
    {
        name: 'Product spec comparison',
        settings: {
            colorScheme: 'light',
            paddingTop: 64,
            paddingBottom: 64,
            width: 'standard',
        },
        // Two demo columns + three demo rows — descriptive placeholder content the seller replaces.
        blocks: [
            { type: 'column', settings: { name: 'Produit A' } },
            { type: 'column', settings: { name: 'Produit B' } },
            {
                type: 'spec-row',
                settings: {
                    label: 'Autonomie',
                    icon: 'battery_full',
                    value1: { format: 'html', html: '<p>12 heures</p>' },
                    value2: { format: 'html', html: '<p>8 heures</p>' },
                },
            },
            {
                type: 'spec-row',
                settings: {
                    label: 'Résistance à l’eau',
                    icon: 'water_drop',
                    value1: { format: 'html', html: '<p>IP55</p>' },
                    value2: { format: 'html', html: '<p>IP67</p>' },
                },
            },
            {
                type: 'spec-row',
                settings: {
                    label: 'Poids',
                    icon: 'scale',
                    value1: { format: 'html', html: '<p>30 g</p>' },
                    value2: { format: 'html', html: '<p>26 g</p>' },
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const specComparisonMeta = {
    label: 'Product spec comparison',
    group: 'Product',
    icon: 'table',
};
//# sourceMappingURL=schema.js.map